/**
 * Company detail modal showing full information including rationale
 */

import React from 'react'
import { X, ExternalLink, Bot, Brain, Calendar, Building2, Tag } from 'lucide-react'
import type { Company } from './types'

interface CompanyDetailProps {
  company: Company
  onClose: () => void
}

export const CompanyDetail: React.FC<CompanyDetailProps> = ({
  company,
  onClose,
}) => {
  const year = company.launched_at
    ? new Date(company.launched_at).getFullYear()
    : null

  const formatDomain = (domain: string) => {
    return domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-primary rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-secondary transition-colors text-secondary hover:text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-secondary">
          <div className="flex items-start gap-4">
            {/* Logo */}
            <div className="w-16 h-16 flex-shrink-0 bg-secondary rounded-xl overflow-hidden flex items-center justify-center">
              {company.logo ? (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-2xl font-bold text-accent">
                  {company.name.charAt(0)}
                </span>
              )}
            </div>

            <div className="flex-1">
              <h2 className="text-2xl font-bold text-primary">{company.name}</h2>
              {company.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline mt-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  {company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              )}

              {/* Tags */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded-full">
                  {formatDomain(company.domain)}
                </span>
                {company.subdomain && (
                  <span className="text-xs px-2 py-1 bg-secondary text-secondary rounded-full">
                    {company.subdomain}
                  </span>
                )}
                {company.is_agent && (
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full flex items-center gap-1">
                    <Bot className="w-3 h-3" />
                    AI Agent
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-secondary uppercase tracking-wide mb-2">
              About
            </h3>
            <p className="text-primary leading-relaxed">{company.desc}</p>
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-secondary" />
              <span className="text-secondary">Batch:</span>
              <span className="text-primary">{company.batch}</span>
            </div>
            {year && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-secondary" />
                <span className="text-secondary">Year:</span>
                <span className="text-primary">{year}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-secondary" />
              <span className="text-secondary">Status:</span>
              <span className={`${company.status === 'Active' ? 'text-green-500' : 'text-secondary'}`}>
                {company.status}
              </span>
            </div>
            {company.industries.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Tag className="w-4 h-4 text-secondary" />
                <span className="text-secondary">Industries:</span>
                <span className="text-primary">{company.industries.join(', ')}</span>
              </div>
            )}
          </div>

          {/* AI Classification Rationale - The key educational content */}
          {company.ai_rationale && (
            <div className="bg-secondary rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-primary">Why This Is an AI Company</h3>
              </div>
              <p className="text-sm text-secondary leading-relaxed">
                {company.ai_rationale}
              </p>
            </div>
          )}

          {/* Agent Classification Rationale */}
          {company.agent_rationale && (
            <div className={`rounded-lg p-4 ${company.is_agent ? 'bg-green-500/10' : 'bg-secondary'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Bot className={`w-5 h-5 ${company.is_agent ? 'text-green-500' : 'text-secondary'}`} />
                <h3 className="font-semibold text-primary">
                  {company.is_agent ? 'Why This Is an AI Agent' : 'Why This Is Not an AI Agent'}
                </h3>
              </div>
              <p className="text-sm text-secondary leading-relaxed">
                {company.agent_rationale}
              </p>
            </div>
          )}

          {/* CTA to book */}
          <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 text-center">
            <p className="text-sm text-primary mb-2">
              Learn how we classified 1,795 companies using multi-agent workflows
            </p>
            <a
              href="/#book"
              className="inline-flex items-center gap-2 text-accent font-semibold hover:underline"
            >
              Read Chapter 14: Answering Business Questions from Unstructured Data
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyDetail
